import { useContext, useMemo } from 'react';

import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';

import SelectedEntityContext from '../contexts/SelectedEntityContext';

interface SlideoutBonusApp {
  entity: EntityDetails.Entity | undefined;
  selectedEntity: string | undefined;
  app: EntityDetails.App | undefined;
  isBonusApp: boolean;
  // For a bonus app, the scoring tab's typeId is the bonus program id (see
  // useTabs: bonusPrograms.find((p) => p.id === tab.typeId)).
  scoringTabId: string | undefined;
  bonusProgramId: string | undefined;
}

/**
 * Resolves the bonus entity app for the slideout's currently selected entity —
 * the same app + scoring-tab lookup the slideout's Override button uses. Shared
 * by EntityAppLink (the Override button) and BoardBonusOverviewProvider (which
 * mounts the override state provider for the dedicated in-slideout override
 * workflow). An app counts as a bonus app when it has a
 * `driver-bonus-program-report` tab.
 */
const useSlideoutBonusApp = (): SlideoutBonusApp => {
  const { selectedEntity, entityField } = useContext(SelectedEntityContext);
  const { entityDefinitions, entityDetailsApps } = useContext(
    EntityDefinitionsContext,
  );

  const entity = useMemo<EntityDetails.Entity | undefined>(
    () => entityDefinitions.find((d) => d.primaryField === entityField),
    [entityDefinitions, entityField],
  );

  const app = useMemo<EntityDetails.App | undefined>(() => {
    if (!entity) {
      return undefined;
    }
    return (
      entityDetailsApps.find(
        (a) => a.entityId === entity.id && a.id === entity.defaultAppId,
      ) ?? entityDetailsApps.find((a) => a.entityId === entity.id)
    );
  }, [entity, entityDetailsApps]);

  const scoringTab = useMemo(
    () => app?.tabs.find((t) => t.type === 'driver-bonus-program-report'),
    [app],
  );

  return useMemo(
    () => ({
      entity,
      selectedEntity,
      app,
      isBonusApp: !!scoringTab,
      scoringTabId: scoringTab?.typeId,
      bonusProgramId: scoringTab?.typeId,
    }),
    [entity, selectedEntity, app, scoringTab],
  );
};

export default useSlideoutBonusApp;
